import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DocumentList from './DocumentList';  // New import
import BalanceTable from './BalanceTable';  // New import
import Navbar from './Navbar';
import './Navbar.css';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import DataTable from 'react-data-table-component';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

function CandidateDetail() {
  const { acctNum: paramAcctNum } = useParams();  // Rename for clarity
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contributors, setContributors] = useState([]);

  // Function to create custom cluster icons
  const createClusterCustomIcon = (cluster) => {
    const count = cluster.getChildCount();
    let size = 40;
    let backgroundColor = 'rgba(51, 136, 255, 0.6)';

    if (count < 10) {
      size = 30;
      backgroundColor = 'rgba(40, 167, 69, 0.6)'; // green for small
    } else if (count < 100) {
      size = 35;
      backgroundColor = 'rgba(255, 193, 7, 0.6)'; // yellow for medium
    } else {
      size = 40;
      backgroundColor = 'rgba(220, 53, 69, 0.6)'; // red for large
    }

    return new L.DivIcon({
      html: `<div style="background-color: ${backgroundColor}; border-radius: 50%; width: ${size}px; height: ${size}px; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: ${size/3}px; border: 2px solid white;">${count}</div>`,
      className: 'marker-cluster-custom',
      iconSize: new L.Point(size, size)
    });
  };

  // Fix Leaflet icon issue
  useEffect(() => {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    });
  }, []);

  // Parse acctNum early to validate
  const acctNum = paramAcctNum ? parseInt(paramAcctNum, 10) : NaN;
  console.log('Raw paramAcctNum:', paramAcctNum, 'Parsed acctNum:', acctNum);  // Debug: Log both

  useEffect(() => {
    const fetchData = async () => {
      if (isNaN(acctNum) || acctNum <= 0) {
        console.warn('Invalid acctNum, skipping fetch:', acctNum);  // Debug
        setError('Invalid account number');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        console.log(`Fetching candidate data for: ${paramAcctNum}`);  // Debug
        const response = await fetch(`/api/candidates/${paramAcctNum}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        console.log('Fetched candidate data:', result);  // Debug: Log response
        setData(result);

        // Fetch contributors
        try {
          const contribResponse = await fetch(`/api/contributions/${paramAcctNum}`);
          if (contribResponse.ok) {
            const contribData = await contribResponse.json();
            setContributors(contribData);
            console.log('Contributors:', contribData);
          } else {
            console.error('Failed to fetch contributors');
          }
        } catch (contribErr) {
          console.error('Error fetching contributors:', contribErr);
        }

  // Compute fullName and set page title including office/district per spec
  const fullName = `${result.NameFirst || ''} ${result.NameMiddle ? result.NameMiddle + ' ' : ''}${result.NameLast || ''}`.trim();
  const officeDesc = result.OfficeDesc || result.Office || '';
  const juris = (result.Juris1num !== undefined && result.Juris1num !== null) ? String(result.Juris1num).trim() : '';
  const hasJuris = juris && juris.toLowerCase() !== 'none';
  const titleSuffix = officeDesc ? `Candidate for ${officeDesc}${hasJuris ? ' District ' + juris : ''}` : '';
  document.title = fullName ? `Florida Elections: ${fullName}${officeDesc ? ' for ' + officeDesc : ''}` : 'Candidate Detail';
      } catch (err) {
        console.error('Fetch error:', err);  // Debug
        setError(err.message);
        document.title = 'Candidate Not Found';
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [paramAcctNum, acctNum]);  // Depend on raw and parsed

  if (loading) return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <Navbar />
  <main style={{ maxWidth: '900px', margin: '18px auto', padding: '24px' }}>
        Loading candidate details...
      </main>
    </div>
  );

  if (error || !data || isNaN(acctNum)) return (
    <div style={{ background: '#fff', minHeight: '100vh', color: '#000' }}>
      <Navbar />
  <main style={{ maxWidth: '900px', margin: '18px auto', padding: '24px' }}>
        <section style={{ padding: '20px', borderRadius: 12, boxShadow: '0 1px 6px rgba(16,24,40,0.04)' }}>
          <h2>Candidate Not Found</h2>
          <button onClick={() => navigate(-1)}>Back to List</button>
        </section>
      </main>
    </div>
  );

  // Compute fullName for H1 and h3 text for office/district
  const fullName = `${data.NameFirst || ''} ${data.NameMiddle ? data.NameMiddle + ' ' : ''}${data.NameLast || ''}`.trim();
  const officeDesc = data.OfficeDesc || data.Office || '';
  const juris = (data.Juris1num !== undefined && data.Juris1num !== null) ? String(data.Juris1num).trim() : '';
  const hasJuris = juris && juris.toLowerCase() !== 'none';
  
  // Shorten party descriptions
  let partyPrefix = '';
  if (data.PartyDesc) {
    if (data.PartyDesc === 'Florida Democratic Party') {
      partyPrefix = 'Democratic ';
    } else if (data.PartyDesc === 'No Party Affiliation (Partisan)') {
      partyPrefix = 'Non-Partisan ';
    } else if (data.PartyDesc === 'No Party Affiliation (nonpartisan offices)') {
      partyPrefix = 'Non-Partisan ';
    } else {
      partyPrefix = data.PartyDesc.split('Party')[0] + ' ';
    }
  }
  
  const h3Text = officeDesc ? `${partyPrefix}Candidate for ${officeDesc}${hasJuris ? ' — District ' + juris : ''}` : '';

  // Prepare fields for table (exclude _id, name parts; include computed address)
  const tableData = {
    ...data,
    fullAddress: `${data.Addr1 || ''}${data.Addr2 ? ', ' + data.Addr2 : ''}, ${data.City || ''}, ${data.State || ''} ${data.Zip || ''}`.trim()
  };
  delete tableData._id;

  const fields = Object.entries(tableData)
    .filter(([key]) => key !== 'NameFirst' && key !== 'NameMiddle' && key !== 'NameLast' && key !== 'AccountList')
    .map(([key, value]) => ({ key: key.replace(/([A-Z])/g, ' $1').trim(), value }));

  // Normalize AccountList to handle malformed data (e.g., { 'Campaign Account': 88746 } -> { accountName, accountNum })
  const normalizeAccountList = (rawList) => {
    if (!Array.isArray(rawList)) {
      console.warn('AccountList is not an array:', rawList);
      return [];
    }

    console.log('Raw AccountList from API data:', rawList);  // Debug: Log raw array
    console.log('Types in AccountList:', rawList.map(acc => ({ name: acc?.accountName || 'N/A', num: acc?.accountNum || 'N/A', numType: typeof (acc?.accountNum ?? acc?.[Object.keys(acc)?.[0] ?? '']) })));  // Debug: Types

    return rawList.map(acc => {
      if (!acc || typeof acc !== 'object') {
        console.warn('Skipping invalid account object:', acc);
        return null;
      }

      // Case 1: Standard schema { accountName, accountNum }
      if (acc.accountName !== undefined && acc.accountNum !== undefined) {
        const parsedNum = parseInt(acc.accountNum, 10);
        if (!isNaN(parsedNum) && parsedNum > 0) {
          return {
            accountName: acc.accountName,
            accountNum: parsedNum
          };
        }
      }

      // Case 2: Malformed { 'Campaign Account': 88746, _id? } or similar dynamic key
      const entries = Object.entries(acc).filter(([key]) => key !== '_id');
      if (entries.length === 1) {
        const [dynamicName, dynamicNum] = entries[0];
        const parsedNum = parseInt(dynamicNum, 10);
        if (!isNaN(parsedNum) && parsedNum > 0) {
          return {
            accountName: dynamicName,
            accountNum: parsedNum
          };
        }
      }

      // Case 3: Multi-key malformed? Log and skip (extend if needed for complex cases)
      console.warn('Unable to normalize account:', acc);
      return null;
    }).filter(Boolean);  // Remove nulls
  };

  // Safe accountList for BalanceTable (normalized)
  let safeAccountList = normalizeAccountList(data.AccountList || []);
  if (safeAccountList.length === 0) {
    // Fallback to single campaign account using candidate's AcctNum
    safeAccountList = [{ accountName: 'Campaign Account', accountNum: acctNum }];
  }
  console.log('Normalized safeAccountList for BalanceTable:', safeAccountList);  // Debug: Log passed prop

  // DataTable columns for contributions
  const columns = [
    {
      name: 'Date',
      selector: row => row.date,
      sortable: true,
      width: '120px',
    },
    {
      name: 'Amount',
      selector: row => row.amount,
      sortable: true,
      format: row => `$${row.amount ? row.amount.toLocaleString() : 'N/A'}`,
      width: '100px',
    },
    {
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      width: '200px',
      cell: row => row.voterId ? (
        <a 
          href={`https://www.flelections.com/donor/${row.voterId}`} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#2563eb', textDecoration: 'none' }}
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        >
          {row.name}
        </a>
      ) : row.name
    },
    {
      name: 'City',
      selector: row => row.city,
      sortable: true,
      width: '150px',
    },
    {
      name: 'State',
      selector: row => row.state,
      sortable: true,
      width: '80px',
    },
    {
      name: 'Occupation',
      selector: row => row.occupation,
      sortable: true,
      width: '150px',
    },
  ];

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh', color: '#000' }}>
      <Navbar />

  <main style={{ maxWidth: '1000px', margin: '18px auto', padding: '24px' }}>
        <section style={{ padding: '20px', borderRadius: 12, boxShadow: '0 1px 6px rgba(16,24,40,0.04)' }}>
          <h2 style={{ marginTop: 0 }}>{fullName}</h2>
          {h3Text && <h3 style={{ marginTop: 6, color: '#444', fontWeight: 500 }}>{h3Text}</h3>}

          <div style={{ marginTop: 12 }}>
            <BalanceTable accountList={safeAccountList} />
          </div>

          <div style={{ marginTop: 12 }}>
            <h3>Contributors Map</h3>
            <div style={{ height: '400px', width: '100%', position: 'relative' }}>
              <MapContainer center={[28.5383, -81.3792]} zoom={7} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <MarkerClusterGroup iconCreateFunction={createClusterCustomIcon}>
                  {contributors.filter(coord => typeof coord.lat === 'number' && typeof coord.lng === 'number').map((coord, index) => (
                    <Marker key={index} position={[coord.lat, coord.lng]}>
                      <Popup>
                        <strong>{coord.name}</strong><br />
                        <hr style={{ margin: '2px 0', border: '1px solid #000' }} />
                        {coord.date}: ${coord.amount ? coord.amount.toLocaleString() : 'N/A'}
                      </Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              </MapContainer>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <h3>Campaign Contributions</h3>
            <DataTable
              columns={columns}
              data={contributors}
              pagination
              paginationPerPage={25}
              defaultSortFieldId={1}
              defaultSortAsc={false}
            />
          </div>

          {/* Documents and balances */}
          <div style={{ marginTop: 16 }}>
            <DocumentList acctNum={acctNum} />
          </div>

          <button onClick={() => navigate(-1)} style={{ marginTop: '20px' }}>Back to List</button>
        </section>
      </main>
    </div>
  );
}

export default CandidateDetail;